import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { prixConfigures } from "@/lib/roof";

/**
 * Liste de démarrage du tableau de bord.
 *
 * Un compte neuf arrive sur des indicateurs à zéro sans savoir par où
 * commencer, et le seul signal qu'il reçoit aujourd'hui est un mur quand il
 * clique « nouvelle soumission ». Cette liste donne l'ordre des choses AVANT
 * qu'il se cogne dessus.
 *
 * Rien ne se coche à la main : chaque étape lit l'état réel du compte. Une
 * case qu'on peut cocher soi-même ment tôt ou tard, et c'est justement sur ces
 * étapes-là (les prix) qu'un mensonge coûte cher. La liste disparaît d'elle-même
 * quand tout est fait — on ne garde pas un échafaudage une fois la maison bâtie.
 *
 * Autonome (son propre dictionnaire), comme AlertePrix et Faq.
 */
const T = {
  fr: {
    titre: "Pour bien démarrer",
    sous: "Quatre étapes, une quinzaine de minutes. Chacune se coche toute seule.",
    reste: (n) => (n === 1 ? "1 étape restante" : `${n} étapes restantes`),
    masquer: "Masquer",
    etapes: {
      entreprise: {
        titre: "Vos coordonnées et votre logo",
        corps: "Ce sont elles qui apparaissent en tête de chaque soumission envoyée à vos clients.",
        lien: "Remplir",
      },
      prix: {
        titre: "Vos prix et votre main-d'œuvre",
        corps: "Rien n'est pré-rempli : l'outil ne connaît pas votre marché. Profitez-en pour régler votre taxe et votre marge, dans le même écran.",
        lien: "Entrer mes prix",
      },
      produits: {
        titre: "Vos bardeaux et vos membranes",
        corps: "Les modèles que vous posez, avec leur prix et leurs couleurs. Vous choisirez ensuite lesquels pour chaque propriété.",
        lien: "Monter mon catalogue",
      },
      premiere: {
        titre: "Votre première soumission",
        corps: "Tapez une adresse : le satellite mesure le toit, pan par pan. Vous vérifiez que c'est le bon bâtiment, vous décochez ce que vous ne refaites pas, et vous ajoutez les cabanons. Essayez sur une maison que vous connaissez — c'est le meilleur moyen de vérifier vos chiffres.",
        lien: "Commencer",
      },
    },
  },
  en: {
    titre: "Getting started",
    sous: "Four steps, about fifteen minutes. Each one ticks itself off.",
    reste: (n) => (n === 1 ? "1 step left" : `${n} steps left`),
    masquer: "Hide",
    etapes: {
      entreprise: {
        titre: "Your details and logo",
        corps: "These are what appear at the top of every quote you send a client.",
        lien: "Fill in",
      },
      prix: {
        titre: "Your prices and labor rate",
        corps: "Nothing is pre-filled: the tool does not know your market. Set your tax and your margin while you are there — same screen.",
        lien: "Enter my prices",
      },
      produits: {
        titre: "Your shingles and membranes",
        corps: "The products you actually install, with their price and colors. You then pick one per property.",
        lien: "Build my catalog",
      },
      premiere: {
        titre: "Your first quote",
        corps: "Type an address: the satellite measures the roof, face by face. You check it picked the right building, untick what you are not redoing, and add the sheds. Try it on a house you know — best way to sanity-check your numbers.",
        lien: "Start",
      },
    },
  },
  es: {
    titre: "Para empezar bien",
    sous: "Cuatro pasos, unos quince minutos. Cada uno se marca solo.",
    reste: (n) => (n === 1 ? "Queda 1 paso" : `Quedan ${n} pasos`),
    masquer: "Ocultar",
    etapes: {
      entreprise: {
        titre: "Sus datos y su logotipo",
        corps: "Es lo que aparece en el encabezado de cada cotización que envía a sus clientes.",
        lien: "Completar",
      },
      prix: {
        titre: "Sus precios y su mano de obra",
        corps: "Nada viene pre-llenado: la herramienta no conoce su mercado. Aproveche para fijar su impuesto y su margen, en la misma pantalla.",
        lien: "Ingresar mis precios",
      },
      produits: {
        titre: "Sus tejas y membranas",
        corps: "Los productos que realmente instala, con su precio y sus colores. Luego elige uno por propiedad.",
        lien: "Armar mi catálogo",
      },
      premiere: {
        titre: "Su primera cotización",
        corps: "Escriba una dirección: el satélite mide el techo, faldón por faldón. Usted verifica que sea el edificio correcto, desmarca lo que no va a rehacer y agrega los cobertizos. Pruébelo en una casa que conozca: es la mejor forma de verificar sus cifras.",
        lien: "Comenzar",
      },
    },
  },
};

const CLE_MASQUE = "rb_demarrage_masque";

/** Au moins un produit du catalogue a un prix : sans ça, rien à vendre. */
function produitsChiffres(s) {
  return (s?.choix || []).some((g) =>
    (g.options || []).some((o) => Number(o.price_per_square) > 0 || Number(o.cost_per_square) > 0)
  );
}

/** L'en-tête de soumission tient debout : un nom, et un moyen de rappeler. */
function entrepriseRemplie(s) {
  return !!(s?.company_name || "").trim() &&
    !!((s?.company_phone || "").trim() || (s?.company_email || "").trim());
}

export default function Demarrage({ settings, nbEstimations = 0, className = "" }) {
  const { lang } = useI18n();
  const [masque, setMasque] = useState(() => {
    try { return localStorage.getItem(CLE_MASQUE) === "1"; } catch { return false; }
  });

  // Réglages pas encore chargés : on n'affiche rien plutôt qu'une liste qui
  // se re-coche sous les yeux du couvreur une seconde plus tard.
  if (!settings || masque) return null;

  const L = T[lang] || T.en;
  const etapes = [
    { cle: "entreprise", fait: entrepriseRemplie(settings), vers: "/settings/entreprise" },
    { cle: "prix", fait: prixConfigures(settings), vers: "/settings/prix" },
    { cle: "produits", fait: produitsChiffres(settings), vers: "/settings/prix" },
    { cle: "premiere", fait: nbEstimations > 0, vers: "/new" },
  ];
  const restants = etapes.filter((e) => !e.fait).length;
  if (restants === 0) return null;   // tout est fait : l'échafaudage s'enlève

  const cacher = () => {
    try { localStorage.setItem(CLE_MASQUE, "1"); } catch { /* navigation privée */ }
    setMasque(true);
  };

  return (
    <div
      data-testid="demarrage"
      className={`bg-surface border border-line rounded-xl p-4 sm:p-6 shadow-carte ${className}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="font-heading text-xl font-bold tracking-tight">{L.titre}</h2>
          <p className="text-sm text-ink-soft mt-1">{L.sous}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-brand-ink">
            {L.reste(restants)}
          </span>
          <button
            onClick={cacher}
            data-testid="demarrage-masquer"
            title={L.masquer}
            aria-label={L.masquer}
            className="text-ink-faint hover:text-ink transition-colors p-2 -m-2"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <ol className="space-y-3">
        {etapes.map((e, i) => {
          const txt = L.etapes[e.cle];
          return (
            <li
              key={e.cle}
              data-testid={`demarrage-${e.cle}`}
              data-fait={e.fait ? "1" : "0"}
              className={`flex gap-3 sm:gap-4 rounded-lg border p-3 sm:p-4 transition-colors ${
                e.fait ? "border-line bg-hover-strong/40" : "border-line-strong"
              }`}
            >
              <span
                className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
                  e.fait
                    ? "bg-emerald-500/15 text-emerald-500"
                    : "bg-orange-600 text-white"
                }`}
              >
                {e.fait ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              <div className="min-w-0">
                <p className={`font-bold ${e.fait ? "text-ink-soft line-through decoration-1" : "text-ink"}`}>
                  {txt.titre}
                </p>
                {!e.fait && (
                  <>
                    <p className="text-sm text-ink-soft mt-1">{txt.corps}</p>
                    <Link
                      to={e.vers}
                      data-testid={`demarrage-lien-${e.cle}`}
                      className="inline-flex items-center gap-1 mt-3 text-sm font-bold text-brand-ink hover:underline"
                    >
                      {txt.lien} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
